import { useEffect, useState } from "react";
import { getProjectAiaSummary } from "../services/projectService";

export interface MoneyValue {
  amount?: number | null;
  currency?: {
    code?: string;
    symbol?: string;
    name?: string;
  } | null;
}

export interface AiaG702Summary {
  originalContractSum?: MoneyValue;
  sovUpdate?: MoneyValue;
  netChangeByChangeOrders?: MoneyValue;
  contractSumToDate?: MoneyValue;
  totalCompletedAndStored?: MoneyValue;
  retainageRate?: string;
  completedWorkRetainage?: MoneyValue;
  storedAmountRetainage?: MoneyValue;
  totalRetainage?: MoneyValue;
  totalEarnedLessRetainage?: MoneyValue;
  lessPreviousCertificates?: MoneyValue;
  currentPaymentDue?: MoneyValue;
  balanceToFinish?: MoneyValue;
}

export interface AiaG703LineItem {
  aiaItemId?: string;
  aiaItemCode?: number | string;
  itemNo?: number;
  description?: string;
  displayOrder?: number;
  origin?: string;
  changeOrderNumber?: string | null;
  subContractor?: {
    id?: string;
    companyName?: string;
  } | null;
  aiaItem?: {
    id?: string;
    itemCode?: number | string;
    itemName?: string;
    itemLabel?: string;
  };
  sovValue?: MoneyValue;
  previousBilled?: MoneyValue;
  currentApplication?: MoneyValue;
  currentStoredMaterial?: MoneyValue;
  totalCompletedAndStored?: MoneyValue;
  percentageComplete?: number;
  balanceToFinish?: MoneyValue;
  retainageAmount?: MoneyValue;
  hasOpenFlags?: boolean;
  flags?: AiaG703Flag[];
}

export interface AiaG703Flag {
  inspectionLienItemId?: string;
  lienItemId?: string;
  drawId?: string;
  drawNumber?: number;
  inspectionId?: string;
  inspectionNumber?: number;
  inspectionDate?: string;
  inspectorName?: string;
  cscEstimation?: number;
  message?: string | null;
}

export interface ProjectAiaSummaryData {
  g702: AiaG702Summary | null;
  g703: {
    lineItems: AiaG703LineItem[];
    retainageRate?: string;
  };
}

const EMPTY_STATE: ProjectAiaSummaryData = {
  g702: null,
  g703: { lineItems: [], retainageRate: "" },
};

const getErrorMessage = (error: unknown) => {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return "Failed to load AIA summary.";
};

export const useProjectAiaSummary = (
  projectId?: string,
  role: "admin" | "lender" | "owner" | "gc" = "admin",
) => {
  const [summary, setSummary] = useState<ProjectAiaSummaryData>(EMPTY_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isActive = true;

    const loadAiaSummary = async () => {
      if (!projectId) {
        if (!isActive) return;
        setSummary(EMPTY_STATE);
        setError("Project ID is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      const { data, error: requestError } = await getProjectAiaSummary(
        projectId,
        role,
      );

      if (!isActive) return;

      if (requestError) {
        setSummary(EMPTY_STATE);
        setError(getErrorMessage(requestError));
        setLoading(false);
        return;
      }

      setSummary((data?.data as ProjectAiaSummaryData) ?? EMPTY_STATE);
      setLoading(false);
    };

    loadAiaSummary();

    return () => {
      isActive = false;
    };
  }, [projectId, role]);

  return { summary, loading, error };
};
